import React, { useEffect, useState } from 'react'
import { Radio, Select, Tabs, Modal, Spin, message, Button, Tooltip } from 'antd'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { useCaseViewContext } from '../../../context'
import { ReOrderLabModal } from './ReOrderLabModal'
import { REQUEST_STATUS, CaseStatusEnum, LAB_RESOURCE_TYPE } from '../../../constants'



const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    padding: 8px 16px;
  }
`

const CustomRadioGroup = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    border-radius: 4px;
    margin-right: 8px;
  }
`

const NoDocumentMessage = ({
  selectedLabs,
  resourceId,
  resourceType
}) => {
  if (!selectedLabs?.reportId && !selectedLabs?.orderId) return <p>No documents available for this labs request</p>
  if (!resourceId) return <p>No {resourceType} available for this labs request</p>
  return null
}

const SelectLabCompany = ({ onLabCompanySet }) => {
  const { caseDetails, apiClasses } = useCaseViewContext()
  const [labsCompany, setLabsCompany] = useState('LabCorp')
  const [submitting, setSubmitting] = useState(false)

  const options = [
    { label: 'LabCorp', value: 'LabCorp' },
    { label: 'Quest', value: 'Quest' },
    { label: 'Ash', value: 'Ash' },
  ]

  const handleConfirm = async () => {
    if (submitting) return
    if (labsCompany === 'Ash') {
      if (!window.confirm('This will order and ship an at-home test kit. Are you sure?')) return
    }
    setSubmitting(true)
    try {
      await apiClasses.LabsAPI.resetLabOrder(caseDetails.caseId, { newLabsCompany: labsCompany })
      message.success('Lab company set successfully')
      if (onLabCompanySet) onLabCompanySet()
    } catch (err) {
      console.error('Error setting lab company:', err)
      message.error('Error setting lab company')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '12px 0' }}>
      <p><strong>Select a lab company to order from:</strong></p>
      <Select
        id="lab-company-select"
        style={{ width: '240px' }}
        options={options}
        value={labsCompany}
        onChange={setLabsCompany}
      />
      <Button
        id="lab-company-confirm-btn"
        type="primary"
        onClick={handleConfirm}
        loading={submitting}
        style={{ alignSelf: 'flex-start', backgroundColor: '#7BAD7E', borderColor: '#7BAD7E' }}
      >
        Confirm &amp; Order Labs
      </Button>
    </div>
  )
}

const OrderIframe = ({ latestRequest }) => {
  const { caseDetails, apiClasses } = useCaseViewContext()
  const [labOrder, setLabOrder] = useState({
    url: null,
    loading: false
  })

  const fetchLabOrderData = () => {
    if (caseDetails && apiClasses?.LabsAPI) {
      setLabOrder(prev => ({ ...prev, loading: true }))
      
      apiClasses.LabsAPI.fetchLabOrderIframe(caseDetails?.caseId)
        .then(({ data }) => {
          setLabOrder({ url: data.url, loading: false })
        })
        .catch(e => {
          console.error('Error fetching lab order UI:', e)
          message.error(e.response?.data?.message || 'Error fetching lab order UI')
          setLabOrder({ url: null, loading: false })
        })
    }
  }

  useEffect(() => {
    fetchLabOrderData()
  }, [caseDetails?.caseId])

  useEffect(() => {
    const handleReloadLabOrderIframe = (event) => {
      if (event.detail?.caseId === caseDetails?.caseId) {
        fetchLabOrderData()
      }
    }

    window.addEventListener('reloadLabOrderIframe', handleReloadLabOrderIframe)
    
    return () => {
      window.removeEventListener('reloadLabOrderIframe', handleReloadLabOrderIframe)
    }
  }, [caseDetails?.caseId])

  return (
    <Spin spinning={labOrder.loading}>
      <div style={{ display: 'flex', flexDirection: 'column', color: '#000' }}>
        <p><strong>Requested Labs Company: {latestRequest.requestedLabsCompany}</strong></p>
        {labOrder.url && <iframe src={labOrder.url} title="Order Labs" width="100%" height="600px" />}
      </div>
    </Spin>
  )
}

const LabDocs = ({ requests = [], onResetLabOrderStatus }) => {
  const { caseDetails, apiClasses, apiType } = useCaseViewContext()
  const [labs, setLabs] = useState({
    selected: null,
    all: []
  })

  const [resource, setResource] = useState({
    type: LAB_RESOURCE_TYPE.REPORT,
    id: null
  })

  const [documentUrl, setDocumentUrl] = useState(null)
  const [isLabOrderModalOpen, setLabOrderModalOpen] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  const getDocumentURL = async (config) => {
    if (!apiClasses?.LabsAPI) {
      message.error('Labs API not available')
      return
    }

    try {
      const { data } = await apiClasses.LabsAPI.fetchResourcePdf(config)
      setDocumentUrl(data)
    } catch (error) {
      message.error('Error fetching lab document')
      console.error('Error fetching lab document:', error)
    }
  }

  useEffect(() => {
    const mappedLabs = requests
      .map(({
        labResults,
        status,
        requestedLabsCompany,
      }) => {
        return (labResults || []).map(labResult => {
          return {
            _id: labResult._id,
            date: labResult.labOrderPlacedAt || labResult.createdAt,
            reportId: labResult.reportId,
            orderId: labResult.orderId,
            status,
            requestedLabsCompany: labResult.requestedLabsCompany || requestedLabsCompany
          }
        })  
      }).flat()

    const mostRecentLabs = (mappedLabs || []).at(-1)

    setLabs({
      all: mappedLabs,
      selected: mostRecentLabs
    })

    setResource({ 
      id: mostRecentLabs?.reportId || mostRecentLabs?.orderId, 
      type: mostRecentLabs?.reportId ? LAB_RESOURCE_TYPE.REPORT : LAB_RESOURCE_TYPE.ORDER
    })
  }, [requests])

  useEffect(() => {
    if (labs.selected) {
      const {
        reportId,
        orderId, 
        requestedLabsCompany,
      } = labs.selected
      
      const documentId = resource.type === LAB_RESOURCE_TYPE.REPORT ? reportId : orderId
      
      if (documentId) {
        setResource(prev => ({ ...prev, id: documentId }))
        getDocumentURL({ 
          resourceType: resource.type, 
          resourceId: documentId, 
          requestedLabsCompany 
        })
      }
    }
  }, [labs.selected, resource.type])

  const _resetLabOrder = (body) => {
    if (body.newLabsCompany === 'Ash') {
      if (!window.confirm('This will order and ship an at-home test kit. Are you sure?')) return;
    }
    else {
      if (!window.confirm('This will require a follow-up lab order be placed. Are you sure?')) return;
    }


    if (!apiClasses?.LabsAPI) {
      message.error('Labs API not available')
      return
    }

    if (isResetting) return;
    setIsResetting(true);

    apiClasses.LabsAPI.resetLabOrder(caseDetails.caseId, body).then(() => {
      message.success('Lab order reset successfully');
      setLabOrderModalOpen(false)
      setIsResetting(false)
      
      setLabs({
        selected: null,
        all: []
      })
      setResource({
        type: LAB_RESOURCE_TYPE.REPORT,
        id: null
      })
      setDocumentUrl(null)
      
      const reloadLabOrderIframeEvent = new CustomEvent('reloadLabOrderIframe', {
        detail: { caseId: caseDetails.caseId }
      })
      window.dispatchEvent(reloadLabOrderIframeEvent)
      
      if (onResetLabOrderStatus) {
        onResetLabOrderStatus()
      }
    }, (e) => {
      message.error(e.response?.data?.message || 'Error resetting lab')
      setIsResetting(false)
    });
  }

  const handleChangeResourceType = e => setResource(prev => ({ ...prev, type: e.target.value }))
  const handleChangeLabs = val => setLabs(prev => ({ ...prev, selected: labs.all?.find(lab => lab.orderId === val)}))
  const openLabOrderModal = () => setLabOrderModalOpen(true)

  const options = labs.all.map(({ date, orderId }) => ({
    label: dayjs(date).format("MM/DD/YYYY hh:mm A"),
    value: orderId
  }))

  if (!requests.length) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <span>Consultation Date ({options.length} available)</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
        <div>
          <Select
            id="lab-docs-date-select"
            style={{ width: '200px', margin: '10px 0', textTransform: 'capitalize'}}
            options={options}
            value={labs.selected?.orderId}
            onChange={handleChangeLabs}
          />
          <CustomRadioGroup
            id="lab-docs-resource-type-group"
            value={resource.type}
            onChange={handleChangeResourceType}
            type='button'
          >
            { !!labs.selected?.reportId && <Radio.Button id="lab-docs-report-radio" value={LAB_RESOURCE_TYPE.REPORT}>{LAB_RESOURCE_TYPE.REPORT}</Radio.Button> }
            { !!labs.selected?.orderId && <Radio.Button id="lab-docs-order-radio" value={LAB_RESOURCE_TYPE.ORDER}>{LAB_RESOURCE_TYPE.ORDER}</Radio.Button> }
          </CustomRadioGroup>
        </div>
        {apiType === 'admin' &&          
          <>
            <Tooltip title="Order Labs">
              <Button id="lab-docs-order-btn" type="primary" onClick={openLabOrderModal} style={{ backgroundColor: '#7BAD7E', borderColor: '#7BAD7E' }}>
                Order
              </Button>
            </Tooltip>
            <ReOrderLabModal 
              currentLab={labs.selected?.requestedLabsCompany}
              isOpened={isLabOrderModalOpen}
              onCancel={() => setLabOrderModalOpen(false)}
              onOk={(newLabsCompany) => {
                _resetLabOrder({ newLabsCompany })
              }}
            />
          </>
        }
      </div>
      <NoDocumentMessage selectedLabs={labs.selected} resourceId={resource.id} resourceType={resource.type} />
      {!!resource.id && <iframe id='doc-iframe' src={documentUrl} title={`Lab ${resource.type}`} width="100%" height="600px" />}
    </div>
  )
}

export const LabOrderModal = ({ closeLabOrderModal, onResetLabOrderStatus }) => {
  const [activeKey, setActiveKey] = useState("1")
  const { caseDetails, activeRequest, apiClasses, readOnlyMode, apiType, fetchCaseDetails } = useCaseViewContext()
  const labAlreadyOrdered = activeRequest?.status !== REQUEST_STATUS.PENDING_LAB_ORDER
  
  const canOrderLabs = apiType === 'admin'

  const handleResetLabOrderStatus = () => {
    if (onResetLabOrderStatus) {
      onResetLabOrderStatus()
    } else if (fetchCaseDetails) {
      fetchCaseDetails()
    }
  }

  useEffect(() => {
     const newActiveKey = !labAlreadyOrdered && canOrderLabs ? "2" : "1"
    setActiveKey(newActiveKey)
}, [caseDetails, labAlreadyOrdered, readOnlyMode, canOrderLabs])

  return (
    <Modal
      visible
      onCancel={closeLabOrderModal}
      width={1000}
      footer={null}
    >
      <StyledTabs
        alignLeft
        activeKey={activeKey}
        onTabClick={(key) => setActiveKey(key)}
        items={[{
          key: "1",
          label: "Lab Documents",

          children: <LabDocs 
            requests={caseDetails?.requests} 
            onResetLabOrderStatus={handleResetLabOrderStatus}
          />
        }, canOrderLabs && !labAlreadyOrdered && {
          key: "2",
          label: "Lab Order",

          children: activeRequest?.requestedLabsCompany ? (
            <OrderIframe latestRequest={activeRequest} />
          ) : (
            <SelectLabCompany onLabCompanySet={handleResetLabOrderStatus} />
          )
        }]} />
    </Modal>
  );
}
